<template>
  <div class="app-layout">
    <aside
      class="sidebar"
      :class="{ 'is-open': sidebarOpen, 'is-collapsed': desktopCollapsed }"
    >
      <div class="logo">
        <div class="logo-icon">
          <i class="el-icon-s-data" aria-hidden="true"></i>
        </div>
        <div class="logo-text">
          <strong>前制程零件投诉</strong>
          <span>Part Complaint</span>
        </div>
      </div>

      <nav class="menu">
        <div v-for="group in menuGroups" :key="group.title" class="menu-block">
          <div class="menu-group">
            <span class="mg-dot" :style="{ background: group.dot }"></span>
            <span class="mg-label">{{ group.title }}</span>
          </div>
          <div
            v-for="item in group.items"
            :key="item.key"
            class="menu-item"
            :class="{ active: isActive(item), 'is-disabled': item.disabled }"
            @click="onMenuClick(item)"
          >
            <span class="mi-icon">
              <i :class="item.icon" aria-hidden="true"></i>
            </span>
            <span class="mi-label">{{ item.label }}</span>
            <span v-if="item.disabled" class="mi-tag">待接入</span>
          </div>
        </div>
      </nav>
    </aside>

    <div v-if="sidebarOpen && isMobile" class="sidebar-backdrop" @click="sidebarOpen = false"></div>

    <section class="main">
      <header class="topbar">
        <div class="topbar-left">
          <button
            class="nav-toggle"
            type="button"
            :title="navToggleLabel"
            :aria-label="navToggleLabel"
            @click="toggleSidebar"
          >
            <i :class="navToggleIcon" aria-hidden="true"></i>
          </button>
          <span class="tb-title">{{ pageTitle }}</span>
        </div>
      </header>
      <main class="view">
        <router-view />
      </main>
    </section>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';

import './default-layout.css';

interface NavItem {
  key: string;
  label: string;
  icon: string;
  route: string;
  disabled?: boolean;
}

interface NavGroup {
  title: string;
  dot: string;
  items: NavItem[];
}

const MOBILE_MAX = 760;

export default Vue.extend({
  name: 'DefaultLayout',
  data() {
    return {
      sidebarOpen: false,
      sidebarCollapsed: false,
      isMobile: false,
      menuGroups: [
        {
          title: '业务',
          dot: '#7e8b9c',
          items: [
            {
              key: 'overview',
              label: '总览',
              icon: 'el-icon-s-home',
              route: '/overview',
              disabled: true,
            },
            {
              key: 'dashboard',
              label: '闭环看板',
              icon: 'el-icon-s-grid',
              route: '/dashboard',
            },
            {
              key: 'data-ops',
              label: '投诉明细',
              icon: 'el-icon-s-operation',
              route: '/data-ops',
            },
          ],
        },
        {
          title: '新系统',
          dot: '#3b6fe0',
          items: [
            {
              key: 'live-board',
              label: '看板',
              icon: 'el-icon-s-flag',
              route: '/live/board',
            },
            {
              key: 'live-detail',
              label: '明细',
              icon: 'el-icon-document',
              route: '/live/detail',
            },
            {
              key: 'live-editor',
              label: '投诉表编辑',
              icon: 'el-icon-edit-outline',
              route: '/live/editor',
            },
          ],
        },
        {
          title: '闭环执行',
          dot: '#6b8cae',
          items: [
            {
              key: 'dispatch',
              label: '派发中心',
              icon: 'el-icon-s-promotion',
              route: '/dispatch',
              disabled: true,
            },
            {
              key: 'notifications',
              label: '通知中心',
              icon: 'el-icon-bell',
              route: '/notifications',
              disabled: true,
            },
          ],
        },
        {
          title: '系统',
          dot: '#5c6b7a',
          items: [
            {
              key: 'data-source',
              label: '数据源',
              icon: 'el-icon-folder-opened',
              route: '/data-source',
            },
          ],
        },
      ] as NavGroup[],
    };
  },
  computed: {
    desktopCollapsed(): boolean {
      return !this.isMobile && this.sidebarCollapsed;
    },
    navToggleIcon(): string {
      if (this.isMobile) {
        return this.sidebarOpen ? 'el-icon-close' : 'el-icon-s-unfold';
      }
      return this.sidebarCollapsed ? 'el-icon-s-unfold' : 'el-icon-s-fold';
    },
    navToggleLabel(): string {
      if (this.isMobile) {
        return this.sidebarOpen ? '收起导航' : '展开导航';
      }
      return this.sidebarCollapsed ? '展开侧边栏' : '收起侧边栏';
    },
    pageTitle(): string {
      const meta = this.$route.meta as { title?: string };
      return meta.title || '前制程零件投诉';
    },
  },
  mounted() {
    this.updateViewport();
    window.addEventListener('resize', this.updateViewport);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateViewport);
  },
  methods: {
    updateViewport() {
      this.isMobile = window.innerWidth <= MOBILE_MAX;
      if (!this.isMobile) {
        this.sidebarOpen = false;
      }
    },
    toggleSidebar() {
      if (this.isMobile) {
        this.sidebarOpen = !this.sidebarOpen;
      } else {
        this.sidebarCollapsed = !this.sidebarCollapsed;
      }
    },
    isActive(item: NavItem): boolean {
      if (item.disabled) return false;
      const path = this.$route.path;
      if (path === item.route) return true;
      return path === '/' && item.key === 'dashboard';
    },
    onMenuClick(item: NavItem) {
      if (item.disabled) {
        this.$message.info('该页面将在完整版接入。');
        return;
      }
      if (this.$route.path !== item.route) {
        this.$router.push(item.route);
      }
      if (this.isMobile) {
        this.sidebarOpen = false;
      }
    },
  },
});
</script>
